import React from 'react';
import CaddieGames from './CaddieGames/CaddieGames';
import CaddieGamesAdmin from './CaddieGames/CaddieGamesAdmin';
import SiteCheck from './SiteCheck/SiteCheck';
import ManageSiteCheck from './SiteCheck/ManageSiteCheck';
import ManagePayFlow from './ManagePayFlow';
import PayFlow from './PayFlow';
import ManageTOSP from './TOSP/ManageTOSP';
import TospPortal from './TOSP/portal/TospPortal';
import MapleCoin from './MapleCoin/MapleCoin';
import ManageMapleCoin from './MapleCoin/manage/ManageMapleCoin';

interface ModulePlaceholderProps {
  name: string;
  /** The catalog route this placeholder is rendering for, when known. */
  route?: string | null;
}

// Some scaffolded modules have a real implementation living under a sibling
// folder. Dispatch on the module name so the user-side route renders the real
// UI, while the admin-side route (rendered with the "Manage <name>" prefix)
// stays as a placeholder until that side is built.
const realImplementations: Record<string, React.ComponentType> = {
  caddiegames: CaddieGames,
  managecaddiegames: CaddieGamesAdmin,
  sitecheck: SiteCheck,
  managesitecheck: ManageSiteCheck,
  // PayFlow: admin sheet + user-side manager review screen.
  managepayflow: ManagePayFlow,
  payflow: PayFlow,
};

// TOSP is scaffolded purely from the module catalog, so its name and routes are
// editable by an admin and can't be relied on as exact keys. Match on any of
// the identifying strings instead — the same approach `isVerityModule` in
// App.js takes — so a catalog rename can't strand the module on this
// placeholder. "Manage" separates the two sides: the admin module from the
// tenant portal.
const isTosp = (name: string, route?: string | null) => /tosp/i.test(`${name} ${route ?? ''}`);
const isManaged = (name: string, route?: string | null) => /manage/i.test(`${name} ${route ?? ''}`);

// MapleCoin, matched the same way: the customer account, and on the Manage
// side the staff customer-servicing screen.
const isMapleCoin = (name: string, route?: string | null) => /maple/i.test(`${name} ${route ?? ''}`);

export default function ModulePlaceholder({ name, route }: ModulePlaceholderProps) {
  if (isTosp(name, route)) {
    return isManaged(name, route) ? <ManageTOSP /> : <TospPortal />;
  }
  if (isMapleCoin(name, route)) {
    return isManaged(name, route) ? <ManageMapleCoin /> : <MapleCoin />;
  }

  const key = name.replace(/\s+/g, '').toLowerCase();
  const Real = realImplementations[key];
  if (Real) return <Real />;

  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>{name}</h2>
      <p style={{ color: '#9aa0a6', fontSize: '1rem', marginTop: '2rem' }}>
        {name} will appear here soon.
      </p>
    </div>
  );
}
